<template>
<div>
  <canvas id="canvas" class="block w-full h-4"></canvas> 
  <div class="container mx-auto px-4 mt-8">
    <h1 class="text-3xl pb-3">Vue Palette Builder</h1>
    <p>
      Vue Palette Builder is a VueJS component for creating color palettes. Create a selection of colors using <a href="https://github.com/xiaokaike/vue-color">vue-color</a>, and get an array of color values.
    </p>
    <!-- <div class="my-4"><a href="#demo" class="mr-4">Demo</a> <a href="#output" class="mx-4"> Output</a> <a href="#installation" class="mx-4">Installation</a></div> -->
    <h2 class="text-3xl pb-3" id="#demo">Demo</h2>
    <div class="flex flex-col sm:flex-row items-start border border-gray-300 rounded p-8">
      <PaletteBuilder 
        v-on:get-palette="palette = $event"
        :pickerType="selectedPicker"
        :initialPalette="[{'hsl':{'h':0,'s':1,'l':0.5,'a':1},'hex':'#FF0000','hex8':'#FF0000FF','rgba':{'r':255,'g':0,'b':0,'a':1},'hsv':{'h':0,'s':1,'v':1,'a':1},'oldHue':0,'source':'hsva','a':1},{'hsl':{'h':120,'s':1,'l':0.5,'a':1},'hex':'#00FF00','hex8':'#00FF00FF','rgba':{'r':0,'g':255,'b':0,'a':1},'hsv':{'h':120,'s':1,'v':1,'a':1},'oldHue':120,'source':'hsva','a':1},{'hsl':{'h':240,'s':1,'l':0.5,'a':1},'hex':'#0000FF','hex8':'#0000FFFF','rgba':{'r':0,'g':0,'b':255,'a':1},'hsv':{'h':240,'s':1,'v':1,'a':1},'oldHue':240,'source':'hsva','a':1},{'hsl':{'h':60,'s':1,'l':0.5,'a':1},'hex':'#FFFF00','hex8':'#FFFF00FF','rgba':{'r':255,'g':255,'b':0,'a':1},'hsv':{'h':60,'s':1,'v':1,'a':1},'oldHue':60,'source':'hsva','a':1}]"
        id="colorpicker"
        class="pb-8 w-full max-w-sm"
      />
      <div class="sm:px-8">
        <p class="mt-0">Choose a picker style, and click on a color to see it in action.</p>
        <div class="inline-block relative w-64">
          <select v-model="selectedPicker" class="block appearance-none w-full bg-white border border-gray-400 hover:border-gray-500 px-4 py-2 pr-8 rounded shadow leading-tight focus:outline-none focus:shadow-outline">
            <template v-for="picker in pickerType">
              <option :value="picker">{{picker}}</option>
            </template>
          </select>  
          <div class="pointer-events-none absolute inset-y-0 right-0 top-0 flex items-center px-2 text-gray-700">
            <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/></svg>
          </div>
        </div>  
      </div>    
    </div>
    <h2 class="text-3xl pb-3" id="#output">Output</h2>
    <p>Vue Palette Builder outputs an array of objects that contain a variety of different color formats, such as Hex and RGBA. Here is the output generated by the current selection in the demo above.</p>
    <pre class="h-64">{{palette}}</pre>
    <p>You can use these results to do whatever you want. This is an HTML canvas with a gradient of the colors selected in the Palette Builder. </p>
    <canvas id="canvas2" class="rounded block w-full h-32"></canvas>    
    <h2 class="text-3xl pb-3 pt-8" id="installation">Installation</h2>
    <p>Install on the command line with: </p>
    <pre>npm i vue-palette-builder</pre>
    <p>Import the component in your Vue app:</p>
    <pre>
import PaletteBuilder from 'vue-palette-builder' // import the component

export default {
  components: {
    PaletteBuilder // register the component
  },
  data() { 
    return {
      palette: [] // create a variable called "palette" to hold the output
    }
  }
}
    </pre>
    <p>Add the component to your markup:</p>
    <pre>
      {{componentHtml}}
    </pre>
    <p>Now the "palette" variable holds the output of whatever palette is built. You can watch it and do something when it is update by assigning a watcher:</p>
    <pre>
watch: {
  palette: function() {
    // do whatever you want
  }
},
    </pre>
    <h2 class="text-3xl pb-3" id="#options">Options</h2>
    <ul>
      <li><strong>pickerType:</strong> Chrome, Sketch, Slider, Swatches, Compact, Material (see <a href="https://github.com/xiaokaike/vue-color">vue-color</a>)</li>
      <li><strong>initialPalette:</strong> An initial palette for the palette builder to start with, as an array of hex values in the following format: <code>[{'hex':'#FF0000'}]</code></li>
    </ul>    
    <hr class="my-16"/>
    <p>Vue Palette Builder was built by <a href="https://alexharris.online">Alex Harris</a>  </p>

    <p></p>
  </div>
</div>
</template>

<script>

const tailwind = require('tailwindcss');
import PaletteBuilder from 'vue-palette-builder'

export default {
  name: 'app',
  components: {
    PaletteBuilder
  },
  data() { 
    return {
      palette: [],
      pickerType: ['Chrome', 'Material', 'Compact', 'Swatches', 'Slider', 'Sketch'],
      selectedPicker: 'Chrome',
      componentHtml: `
<PaletteBuilder 
  v-on:get-palette="palette = $event"
  :pickerType="selectedPicker"
  :initialPalette="[{'hex':'#FF0000'}]"        
  id="colorpicker"
  class="pb-8 w-full max-w-sm"
/>`  
    }
  },
  watch: {
    palette: function() {
      this.buildCanvas()
    }
  },
  mounted() {
    this.buildCanvas()     
  },
  methods: {
    buildCanvas() {
        var canvas = document.getElementById('canvas');
        var ctx = canvas.getContext('2d');

        var canvas2 = document.getElementById('canvas2');
        var ctx2 = canvas2.getContext('2d');

        // Create a linear gradient
        var gradient = ctx.createLinearGradient(0,0, 400,0);        
        var gradient2 = ctx2.createLinearGradient(0,0, 400,0);


        // Add color stops
        this.palette.forEach((v, i) => {
          var position = (i) / (this.palette.length)
          
          gradient.addColorStop(position, v['hex']);
          gradient2.addColorStop(position, v['hex']);

        })
        
        // Set the fill style and draw a rectangle
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, 400, 400);      

        ctx2.fillStyle = gradient;
        ctx2.fillRect(0, 0, 400, 400);   
    }
  }
}
</script>

<style scoped>

@tailwind base;
@tailwind components;
@tailwind utilities;

a {
  @apply text-red-700 underline
}

p {
  @apply my-8
}

pre {
  @apply rounded bg-gray-200 p-2 mb-3 overflow-scroll text-sm
}
/* #colorpicker {
  width: 400px;
} */
</style>

